import Vue from 'vue';
import { cloneDeep } from 'lodash';
import { commits, dispatches, getters } from '@/modules/core/app/helpers/store';
import { shallowCopyKeyedObject } from '@/modules/core/helper';
import { WidgetEvent, WidgetState } from '@/modules/ta/widget/widget.constants';
import EventBus from '@/modules/core/app/helpers/EventBus';
import { IsotopeConstants } from '@/modules/core/app/constants/isotope.constants';

export const mutations = {
  setWidgetInLayout(state, { layoutId, widget }) {
    Vue.set(state.layouts[layoutId].widgets, widget.id, widget);
    dispatches.widget.updateWidget(widget);
    // Don't replace entire layouts object — it causes unnecessary re-renders
    // of unrelated charts (similar to FreeFlowGrid resize fix in PR #46513)
  },
  removeWidgetInLayout(state, { layoutId, widget }) {
    if (state.layouts[layoutId] && Object.hasOwn(state.layouts[layoutId].widgets, widget.id)) {
      // Replace the widgets object reference so Vue 2 detects the removal
      // (Vue 2 cannot detect property deletion via `delete` on an existing object)
      const updatedWidgets = shallowCopyKeyedObject(state.layouts[layoutId].widgets);
      delete updatedWidgets[widget.id];
      // Replace only this layout's widgets, not the entire layouts collection
      // This prevents unnecessary re-renders of unrelated charts
      state.layouts[layoutId].widgets = updatedWidgets;
    }
    commits.widget.removeWidget({ widget });
  },
  setLayoutsFromDashboard(state, { dashboard }) {
    commits.widget.setWidgetsFromDashboard({ dashboard });

    state.layouts = {};
    Object.values(dashboard.layouts).forEach((layout) => {
      if (layout?.widgets) {
        layout.widgets = Object.values(layout.widgets).reduce((accum, widget) => {
          accum[widget.id] = widget;
          return accum;
        }, {});
        state.layouts[layout.id] = layout;
      }
    });

    // signal to update layout when widgets are updated in a layout
    setTimeout(() => {
      EventBus.signal(WidgetEvent.UPDATE_DISPLAY_ORDER, true);
    }, IsotopeConstants.DISPLAY_ORDER_TIMEOUT);
  },
  saveList(state, layouts) {
    layouts.forEach((layout) => {
      layout.widgets = getters.dashboardLayout
        .getWidgetsInLayout(layout.id)
        .reduce((accum, { id }) => {
          accum[id] = null;
          return accum;
        }, {});
      state.layouts[layout.id] = layout;
    });
    state.layouts = shallowCopyKeyedObject(state.layouts);
  },
  setLayout(state, layout) {
    layout.widgets = Object.keys(layout.widgets || []).reduce((accum, widgetId) => {
      accum[widgetId] = null;
      return accum;
    }, {});
    state.layouts[layout.id] = layout;
    state.layouts = shallowCopyKeyedObject(state.layouts);
  },
  updateTitleAndOrder(layoutState, updatedLayouts) {
    updatedLayouts.forEach((layout) => {
      const updatedLayout = layoutState.layouts[layout.id];
      updatedLayout.display_order = layout.display_order;
      updatedLayout.title = layout.title;
    });
  },
  clearChartPaletteFromWidgets() {
    const widgets = [];
    getters.dashboard.getLayoutsInDashboard().forEach((layout) => {
      getters.dashboardLayout.getWidgetsInLayout(layout.id).forEach((widget) => {
        widget.metadata.chart_palette = null;
        widgets.push(widget);
      });
    });

    commits.widget.updateWidgets(widgets);
  },
  addGhostWidget(layoutState, { layoutId, widget, state = WidgetState.STATE_DONE }) {
    layoutState.layouts[layoutId].widgets[widget.id] = widget;
    layoutState.layouts[layoutId].widgets = shallowCopyKeyedObject(
      layoutState.layouts[layoutId].widgets
    );

    commits.widget.setWidget({ widget, state });
    commits.widgetBuilder.setBuilderForm({ widget });
  },
  addWidget(
    layoutState,
    { layoutId, widget, state = WidgetState.STATE_DONE, isDuplicated = false }
  ) {
    if (!layoutState.layouts || !layoutState.layouts[layoutId]) {
      layoutState.layouts[layoutId] = {
        widgets: {},
      };
    }
    layoutState.layouts[layoutId].widgets[widget.id] = widget;
    commits.widget.setWidget({ widget, state });
    layoutState.layouts[layoutId].widgets = shallowCopyKeyedObject(
      layoutState.layouts[layoutId].widgets
    );

    if (isDuplicated) {
      EventBus.signal(WidgetEvent.DUPLICATED, widget);
    }
  },
  /**
   * @param {LayoutState} state
   * @param map
   */
  setOriginalLayoutWidgets(state, widgets) {
    state.originalLayoutWidgets = cloneDeep(widgets);
  },

  insertLayout(state, { layout }) {
    const layoutId = layout.id;

    layout.widgets = Object.keys(layout.widgets || []).reduce((accum, widgetId) => {
      accum[widgetId] = null;
      return accum;
    }, {});

    state.layouts[layoutId] = layout;
    state.layouts = shallowCopyKeyedObject(state.layouts);
  },
  removeLayout(state, layoutId) {
    const layout = state.layouts[layoutId];
    const widgetIds = Object.keys(layout.widgets);

    commits.widget.removeWidgetsFromId({ widgetIds });
    delete state.layouts[layoutId];
    state.layouts = shallowCopyKeyedObject(state.layouts);
  },
  setExecutiveSummaryOnLayout(state, executiveSummary) {
    state.layouts[executiveSummary.layout_id].executive_summary = executiveSummary;
  },
  setViewingLayoutPreview(state, value) {
    state.viewingLayoutPreview = value;
  },
};
